import React from "react";
import { Link } from "react-router-dom";
import {
  ExternalLink, User, Building2, Check, Sparkles, MailCheck, ArrowRight,
} from "lucide-react";

const MAILIZ_URL = "https://auth-mailiz.mssante.fr/realms/mssante/protocol/openid-connect/auth?response_type=code&client_id=roundcube&scope=email+profile+openid&redirect_uri=https%3A%2F%2Froundcube.mssante.fr%2Findex.php%2Flogin%2Foauth&state=9t7HFaptjuBg";

/**
 * Helps the visitor self-select between two options:
 *  - Solo professional → Mailiz (free, instant, @pro.mssante.fr)
 *  - Structure / multi-user → MetaConnect (organizational, sub-domain)
 * Builds trust by openly recommending a third-party product when relevant.
 */
export default function MssanteIndividualVsStructure() {
  return (
    <section className="hex-soft py-14 lg:py-20" data-testid="mssante-individual-vs-structure">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#0284C7] mb-3">
            <Sparkles size={13} /> Quelle solution est faite pour vous ?
          </div>
          <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">
            Seul ou à plusieurs sur la boîte mail&nbsp;? Voici ce que nous recommandons
          </h2>
          <p className="mt-3 text-[14px] text-[#64748B] leading-relaxed">
            Le bon choix dépend du <strong className="text-[#0F2B5B]">nombre de personnes</strong> qui
            utiliseront la boîte au quotidien — pas de votre statut juridique. Soyons transparents :
            si une seule personne accède à la messagerie, une solution gratuite tierce suffit.
            Dès que vous êtes deux ou plus, MetaConnect change la donne.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* === Mailiz — solo professional === */}
          <div className="mc-card p-6 lg:p-7 border-[rgba(127,179,255,0.20)]" data-testid="card-mailiz">
            <div className="flex items-start gap-4 flex-wrap mb-4">
              <div className="grid place-items-center h-11 w-11 rounded-lg bg-[rgba(45,123,255,0.12)] border border-[rgba(127,179,255,0.32)] text-[#0284C7] shrink-0">
                <User size={20} />
              </div>
              <div className="flex-1 min-w-[160px]">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#0284C7] mb-1">
                  Vous exercez seul · Aucun collaborateur sur la BAL
                </div>
                <h3 className="font-display text-[#0F2B5B] text-[20px] leading-tight">
                  Une boîte mail personnelle gratuite suffit
                </h3>
              </div>
            </div>

            <p className="text-[13px] text-[#64748B] leading-relaxed mb-5">
              Si vous êtes un <strong className="text-[#0F2B5B]/90">professionnel exerçant seul</strong> —
              opticien en point de vente unique sans collaborateur, professionnel libéral en cabinet
              individuel, etc. — une seule personne reçoit et envoie les messages MSSanté. Dans ce cas,
              vous pouvez créer une boîte personnelle <strong className="text-[#0F2B5B]">instantanément et gratuitement</strong> avec
              <strong className="text-[#0F2B5B]"> Mailiz</strong>, l'opérateur public de l'ANS.
              L'adresse se termine par <code className="font-mono text-[12.5px] px-1.5 py-0.5 rounded bg-[#F1F5F9] border border-[rgba(127,179,255,0.18)]">@pro.mssante.fr</code>.
            </p>

            <div className="rounded-lg bg-white p-4 mb-4 flex items-center justify-center">
              <img src="/logo-mailiz.jpg" alt="Mailiz — La messagerie des ordres en toute confiance"
                   className="h-12 lg:h-14 w-auto" loading="lazy" />
            </div>

            <ul className="grid gap-1.5 mb-5">
              {[
                "Création gratuite et instantanée",
                "Pas de configuration technique",
                "Conforme ANS (opérateur public)",
                "Adresse @pro.mssante.fr (domaine partagé)",
              ].map((it) => (
                <li key={it} className="flex items-start gap-2 text-[13px] text-[#0F2B5B]/85">
                  <Check size={14} className="text-[#0284C7] mt-0.5 shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>

            <a href={MAILIZ_URL} target="_blank" rel="noopener noreferrer"
               data-testid="cta-mailiz-create"
               className="btn-ghost px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5">
              <ExternalLink size={13} /> Créer une boîte gratuite sur Mailiz
            </a>
            <p className="mt-3 text-[11px] text-[#64748B]">
              Mailiz est un service tiers indépendant. MetaConnect n'est pas affilié à Mailiz, mais
              le recommande quand c'est la solution la plus adaptée.
            </p>
          </div>

          {/* === MetaConnect — structure === */}
          <div className="mc-card p-6 lg:p-7 border-[rgba(110,231,197,0.40)] bg-[rgba(110,231,197,0.04)] ring-1 ring-[var(--mc-mint)]"
               data-testid="card-metaconnect-structure">
            <div className="flex items-start gap-4 flex-wrap mb-4">
              <div className="grid place-items-center h-11 w-11 rounded-lg bg-[rgba(110,231,197,0.16)] border border-[rgba(110,231,197,0.45)] text-[var(--mc-mint)] shrink-0">
                <Building2 size={20} />
              </div>
              <div className="flex-1 min-w-[160px]">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-[var(--mc-mint)] mb-1">
                  Vous travaillez à plusieurs · Équipe ou structure
                </div>
                <h3 className="font-display text-[#0F2B5B] text-[20px] leading-tight">
                  Une boîte organisationnelle change tout
                </h3>
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-[rgba(110,231,197,0.45)] text-[var(--mc-mint)] shrink-0">
                Recommandé
              </span>
            </div>

            <p className="text-[13px] text-[#0F2B5B]/85 leading-relaxed mb-5">
              Dès que <strong className="text-[#0F2B5B]">deux personnes ou plus</strong> doivent
              utiliser la même boîte mail (associés, salariés, secrétaire, remplaçant…), une boîte
              organisationnelle MetaConnect s'impose : multi-utilisateurs, sous-domaine métier
              exclusif —
              <code className="font-mono text-[12.5px] mx-1 px-1.5 py-0.5 rounded bg-[#F1F5F9] border border-[rgba(110,231,197,0.20)]">@opticiens.mssante.fr</code>,
              <code className="font-mono text-[12.5px] mx-1 px-1.5 py-0.5 rounded bg-[#F1F5F9] border border-[rgba(110,231,197,0.20)]">@optique.mssante.fr</code>,
              <code className="font-mono text-[12.5px] mx-1 px-1.5 py-0.5 rounded bg-[#F1F5F9] border border-[rgba(110,231,197,0.20)]">@ohmyvue.mssante.fr</code>.
              Que vous soyez un cabinet, un magasin, un centre, une MSP, un EHPAD, une enseigne ou un groupe.
            </p>

            <ul className="grid gap-1.5 mb-5">
              {[
                "Multi-utilisateurs (responsable, cotitulaires, délégataires)",
                "Sous-domaine métier — image professionnelle",
                "Continuité même en cas de turnover ou congés",
                "Information automatique des prescripteurs (OhMyVue / OhMyOuie)",
                "Tarification transparente, sans engagement",
              ].map((it) => (
                <li key={it} className="flex items-start gap-2 text-[13px] text-[#0F2B5B]/90">
                  <Check size={14} className="text-[var(--mc-mint)] mt-0.5 shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-2 flex-wrap">
              <Link to="/mssante/inscription" data-testid="cta-metaconnect-create"
                    className="btn-primary px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5">
                <MailCheck size={13} /> Créer ma boîte MetaConnect
              </Link>
              <Link to="/demo" data-testid="cta-metaconnect-demo"
                    className="btn-ghost px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5">
                Voir une démo <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
